import { Button, Card, CardContent, cn } from "@safeplate/ui";
import { AlertTriangle, CircleCheck, Inbox, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";

import { SlowStartNotice } from "./slow-start.js";

export function LoadingState({
  label = "Loading",
  waking = false,
}: {
  label?: string;
  waking?: boolean;
}) {
  return (
    <div
      aria-live="polite"
      className="grid min-h-40 place-items-center rounded-md border border-stone-200 bg-surface"
      role="status"
    >
      <div className="flex flex-col items-center gap-3 text-sm font-medium text-stone-500">
        <LoaderCircle
          className="size-5 animate-spin text-emerald-800"
          aria-hidden="true"
        />
        {label}
        {waking ? <SlowStartNotice active /> : null}
      </div>
    </div>
  );
}

export function ErrorState({
  actionLabel = "Try again",
  message,
  onAction,
  title = "Couldn’t load this",
}: {
  actionLabel?: string;
  message: string;
  onAction?: () => void;
  title?: string;
}) {
  return (
    <Card className="border-red-200 bg-red-50/60">
      <CardContent className="flex min-h-52 flex-col items-start justify-center gap-4 p-6">
        <span className="grid size-10 place-items-center rounded-xl bg-red-100 text-red-800">
          <AlertTriangle className="size-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-bold text-stone-950">{title}</h2>
          <p className="mt-1 max-w-xl text-sm leading-6 text-stone-600">
            {message}
          </p>
        </div>
        {onAction ? (
          <Button onClick={onAction} variant="outline">
            {actionLabel}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}

export function EmptyState({
  action,
  calm = false,
  description,
  title,
}: {
  action?: ReactNode;
  calm?: boolean;
  description?: string;
  title: ReactNode;
}) {
  const Icon = calm ? CircleCheck : Inbox;
  return (
    <div className="flex min-h-40 flex-col items-center justify-center rounded-md border border-dashed border-stone-300 bg-surface/70 px-6 py-8 text-center">
      <span
        className={cn(
          "mb-3 grid size-10 place-items-center rounded-md",
          calm
            ? "bg-emerald-100 text-emerald-800"
            : "bg-stone-100 text-stone-600",
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
      </span>
      <h2 className="text-base font-bold tracking-tight text-stone-950">
        {title}
      </h2>
      {description ? (
        <p className="mt-2 max-w-md text-sm leading-6 text-stone-500">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}
